import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';
import { FlexRow } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { NoItemsWrapper } from '../../Zemplin/General/NoItemsWrapper';
import { DsiProductTabDefault as ProductTabDefault } from './ProductTabDefault';
import { ZemplinLoaderWrapper } from '../../..';
import { getProductDefaultInfo } from '../../../utilities/selectors';
import { prop, round } from '../../../utilities';
import { productHasGift } from '../Cart/utilities';

interface Props {
  apiUrl: string;
  products: any;
  isFetching: boolean;
  addToCart: any;
  addToRequest: any;
  visible?: boolean;
  addToFavorite: any;
  addToCompare?: any;
  hasFavourite?: boolean;
  canOrder?: boolean;
  showMoc?: boolean;
  showWithVat?: boolean;
  loadingPrices?: boolean;
  hasUser?: boolean;
  isB2b?: boolean;
  showStockCount?: boolean;
  user?: any;
  isCorporate?: boolean;
  domainId?: number;
  conditionTextMapping?: any;
  showCompareIcon?: any;
  compareProductIds?: number[]
}

function ProductList({
  apiUrl,
  products,
  isFetching,
  addToCart,
  addToRequest,
  visible = true,
  addToFavorite,
  addToCompare,
  hasFavourite = false,
  canOrder,
  showMoc,
  showWithVat,
  loadingPrices,
  hasUser,
  isB2b,
  showStockCount,
  user,
  isCorporate,
  domainId,
  conditionTextMapping,
  showCompareIcon,
  compareProductIds
}: Props) {
  if (!visible) {
    return null;
  }
  const renderProductTab = (key, product) => {
    const {
      availid,
      availability,
      picture,
      pictureWebP,
      url,
      name,
      price,
      oldPrice,
      isNew,
      isSale,
      isFavorite,
      salePercentage,
      id,
      description,
      currency,
      variants,
      isSaleB2b,
      isSaleB2c,
      onStock,
      isSecondHand,
      isFreeDelivery,
      imagePath,
      oldPriceCorporate,
      giftSaleData,
      cartSaleData,
      condition,
      isWaiting,
      currentSales,
      plu,
      hiddenSale,
      forbiddenOrderByDate
    } = getProductDefaultInfo(product, apiUrl, showMoc, showWithVat);

    return (
      <ProductTabDefault
        id={id}
        key={key}
        apiUrl={apiUrl}
        imagePath={imagePath}
        picture={picture}
        pictureWebP={pictureWebP}
        price={
          isB2b
            ? showMoc
              ? showWithVat
                ? prop(product, 'moc_price', price)
                : prop(product, 'moc_price_without_vat', price)
              : price
            : price
        }
        oldPrice={
          isSecondHand
            ? '0'
            : isCorporate
            ? oldPriceCorporate
            : isB2b
            ? showWithVat
              ? oldPrice
              : prop(
                  product,
                  'product_detail.main_good.price_without_vat_sale',
                  oldPrice,
                )
            : oldPrice
        }
        availid={availid}
        availability={availability}
        url={url}
        name={name}
        addToCart={addToCart}
        addToRequest={addToRequest}
        isNew={isNew}
        isSale={isSale}
        isFavorite={isFavorite}
        isCompare={compareProductIds && compareProductIds.includes(id)}
        salePercentage={
          isCorporate
            ? 100 - round((price / oldPriceCorporate) * 100, 2)
            : !isB2b && showMoc && showWithVat && oldPrice > price
            ? 100 - round((price / oldPrice) * 100, 2)
            : salePercentage
        }
        addToFavorite={addToFavorite}
        addToCompare={addToCompare}
        product={product}
        hasFavourite={hasFavourite}
        description={description}
        currency={currency}
        canOrder={canOrder}
        forbiddenOrder={forbiddenOrderByDate}
        variants={variants}
        loadingPrices={loadingPrices}
        hasUser={hasUser}
        stockCount={showStockCount ? onStock : 0}
        isSecondHand={isSecondHand}
        isFreeDelivery={isB2b ? false : isFreeDelivery}
        showMoc={showMoc}
        hasGift={
          !!currentSales?.length ||
          productHasGift(giftSaleData, domainId, isB2b, hasUser)
        }
        hasCartSale={productHasGift(cartSaleData, domainId, isB2b, hasUser)}
        condition={condition}
        conditionTextMapping={conditionTextMapping}
        isWaiting={isWaiting}
        managerLink={
          user && user.originalUser
            ? `/manager?customerId=${user.outer_id}-${user.id}&customerAddressId=&query=${plu}`
            : undefined
        }
        hiddenSale={hiddenSale}
        showCompareIcon={showCompareIcon}
      />
    );
  };

  const productsComponent =
    products && products.map((p: any, key) => renderProductTab(key, p));

  return (
    <>
      {isFetching ? (
        <ZemplinLoaderWrapper height={500} />
      ) : productsComponent && productsComponent.length > 0 ? (
        <ProductsCardWrapper visible={visible}>
          {productsComponent}
        </ProductsCardWrapper>
      ) : (
        <NoItemsWrapper height={500} text={__('Nenašli sa žiadne produkty')} />
      )}
    </>
  );
}

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const ProductsCardWrapper = styled(FlexRow)<{ visible: boolean }>`
  margin: ${rem(32)} 0px;
  grid-template-columns: repeat(auto-fill, minmax(${rem(260)}, 1fr));
  gap: 1rem;
  align-items: center;
  grid-auto-rows: minmax(1rem, auto);
  display: grid;

  ${({ theme }) => theme.mediab.m810`
     margin-top: 0;
  `}
`;

const OldProductsCardWrapper = styled(FlexRow)<{ visible: boolean }>`
  flex-flow: row wrap;
  margin-bottom: ${({ visible }) => (visible ? rem(24) : 0)};
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  animation: ${animateIn} 0.2s ease;

  ${({ theme }) => theme.mediab.m810`
      margin-bottom: ${({ visible }) => (visible ? rem(32) : 0)};
  `};
`;

export { ProductList as DsiProductList };
